/**
 * TranslateDropdown — 45+ language selector for the article reader.
 * Fetches supported languages from /api/i18n/languages, calls the
 * per-article cached translation endpoint, and returns the translated
 * fields to the parent via `onTranslated`.
 */
import { useCallback, useEffect, useState } from "react";
import { Languages, Loader2, Check, ChevronDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function TranslateDropdown({
  articleId,
  currentLang,
  onTranslated,
  onReset,
  testIdPrefix = "article-translate",
}) {
  const [langs, setLangs] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API}/i18n/languages`);
        if (!r.ok) return;
        const d = await r.json();
        if (alive) setLangs(d.languages || []);
      } catch (_e) { /* silent */ }
    })();
    return () => { alive = false; };
  }, []);

  const pick = useCallback(async (code) => {
    setOpen(false);
    if (!articleId || !code) return;
    setBusy(code);
    try {
      const r = await fetch(`${API}/articles/${articleId}/translate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ target_lang: code }),
      });
      if (!r.ok) {
        const t = await r.text();
        throw new Error(t || `HTTP ${r.status}`);
      }
      const d = await r.json();
      onTranslated?.({ ...d, target_lang: code });
    } catch (e) {
      window.alert(`Translation failed: ${e.message}`);
    } finally {
      setBusy(null);
    }
  }, [articleId, onTranslated]);

  return (
    <div className="relative inline-block" data-testid={testIdPrefix}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 shadow-sm"
        data-testid={`${testIdPrefix}-btn`}
      >
        <Languages className="w-3.5 h-3.5 text-blue-600" />
        {busy ? <><Loader2 className="w-3 h-3 animate-spin" /> Translating…</>
              : <>Translate {currentLang && <span className="opacity-60">· {currentLang.toUpperCase()}</span>}</>}
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-1 rounded-xl border border-slate-200 bg-white shadow-lg py-1 max-h-80 overflow-auto z-40"
          style={{ minWidth: 240 }}
          data-testid={`${testIdPrefix}-menu`}
        >
          <button
            type="button"
            onClick={() => { setOpen(false); onReset?.(); }}
            className="block w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50"
            data-testid={`${testIdPrefix}-reset`}
          >
            <span className="font-semibold">Original</span>
          </button>
          <div className="border-t border-slate-100 my-1" />
          {langs.map((l) => (
            <button
              key={l.code}
              type="button"
              onClick={() => pick(l.code)}
              disabled={busy === l.code}
              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 flex items-center justify-between ${currentLang === l.code ? "font-semibold text-blue-700" : ""}`}
              data-testid={`${testIdPrefix}-opt-${l.code}`}
            >
              <span>{l.name}</span>
              <span className="text-[10px] font-mono opacity-50">
                {busy === l.code ? <Loader2 className="w-3 h-3 animate-spin inline" /> : currentLang === l.code ? <Check className="w-3 h-3" /> : l.code.toUpperCase()}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
